import styled from 'styled-components';
import React, { useContext } from 'react';
import { useTranslation } from '@lib/useTypedTranslation';
import * as am4charts from '@amcharts/amcharts4/charts';

import { IProcessUsageData } from '../../../../../services/core/deviceProcesses';
import { CloseButton } from '../../../../../components/controls/closeButton';
import { createYAxis } from '../../../../../lib/usageChartYAxis';
import { StackedColumnsDateChart } from '../../../../../components/chart/stackedColumnsDateChart';
import { ChipButton } from '../../../../../components/controls/chipButton';
import { DeviceInfoContext } from '../../index';

import './30DayProcessUsage.css';
import { ChartTimePeriod, formatDurationData, formatTooltipDate } from '../../deviceFormat';
import { drawRelativeXAxisLabels, getDaysOfChartTimePeriod } from '../../../../../components/chart/lib';

interface IProps {
  data: IProcessUsageData,
  handleClose: () => void,
  chartTimePeriod: ChartTimePeriod,
  setChartTimePeriod: (chartTimePeriod: ChartTimePeriod) => void,
  setSeries: (series: Series) => void,
  series: Series
}

export enum Series {
  usage = 'usage',
  cpu = 'cpu'
}

export function ProcessUsage30Days(props: IProps) {
  const timeZoneOffset = useContext(DeviceInfoContext).timeZoneOffset || 0;
  const { t } = useTranslation(['translation', 'performance', 'deviceProcesses', 'timeState']);
  const timePeriodTranslation = props.chartTimePeriod === ChartTimePeriod.last48Hours ? t('OVER_THE_PAST_X_HOURS', { count: 48, ns: 'timeState' }) : t('OVER_THE_PAST_X_DAYS', { count: getDaysOfChartTimePeriod(props.chartTimePeriod), ns: 'timeState' });
  const dailyOrHourlyTranslationContext = props.chartTimePeriod === ChartTimePeriod.last30Days ? 'daily' : 'hourly';

  return (
    <div className="process-usage-chart">
      <CloseButton handleClose={props.handleClose} className='process-usage-chart-close' />
      <div className="core-device_process-30-day-process-usage">
        <StackedColumnsDateChart<any>
          data={props.data.processUsageData.map((data) => ({ ...data, userActivity: data.userActivity || 0, cpuActivity: data.cpuActivity || 0, date: data.date }))}
          buttons={[
            {
              Component: Button,
              props: {
                active: props.chartTimePeriod === ChartTimePeriod.last30Days,
                text: t('DAY_COUNT', { ns: 'timeState', count: 30 }),
                onClick: () => { props.setChartTimePeriod(ChartTimePeriod.last30Days); }
              }
            },
            {
              Component: Button,
              props: {
                active: props.chartTimePeriod === ChartTimePeriod.last7Days,
                text: t('DAYS_7', { ns: 'timeState' }),
                onClick: () => { props.setChartTimePeriod(ChartTimePeriod.last7Days); }
              }
            },
            {
              Component: Button,
              props: {
                active: props.chartTimePeriod === ChartTimePeriod.last48Hours,
                text: t('HOURS_48', { ns: 'timeState' }),
                onClick: () => { props.setChartTimePeriod(ChartTimePeriod.last48Hours); }
              }
            },
            {
              Component: Button,
              props: {
                active: props.series === Series.usage,
                text: t('USAGE', { ns: 'deviceProcesses' }),
                onClick: () => { props.setSeries(Series.usage); }
              }
            },
            {
              Component: Button,
              props: {
                active: props.series === Series.cpu,
                text: t('CPU', { ns: 'translation' }),
                onClick: () => { props.setSeries(Series.cpu); }
              }
            }
          ]}
          days={30}
          series={[{
            visible: props.series === Series.usage,
            dataKey: 'userActivity',
            description: t('IN_USE', { ns: 'performance' }),
            colour: '#4AA4BA'
          },
          {
            visible: props.series === Series.cpu,
            dataKey: 'cpuActivity',
            description: t('CPU_USAGE', { ns: 'deviceProcesses' }),
            colour: '#4AA4BA'
          }]}
          formatData={formatDurationData(t)}
          title={`${props.data.processName} - ${props.series === Series.usage ?
            t(`USAGE_TITLE` as 'USAGE_TITLE_daily' | 'USAGE_TITLE_hourly', { ns: 'deviceProcesses', timePeriod: timePeriodTranslation, context: dailyOrHourlyTranslationContext })
            :
            t(`CPU_USAGE_TITLE` as 'CPU_USAGE_TITLE_daily' | 'CPU_USAGE_TITLE_hourly', { ns: 'deviceProcesses', timePeriod: timePeriodTranslation, context: dailyOrHourlyTranslationContext })
          }`}
          cypressId="process-usage-chart"
          yAxisType='duration'
          drawYAxis={(chart, yAxis) => {
            const dataKey = props.series === Series.usage ? 'userActivity' : 'cpuActivity';
            const maxUserActivitySeconds = Math.max(...chart.data.map((data) => data[dataKey]));
            const maxUserActivityHours = maxUserActivitySeconds / 60 / 60;
            (yAxis as am4charts.DurationAxis).baseUnit = "second";

            createYAxis(yAxis as am4charts.DurationAxis, maxUserActivityHours, t);

            yAxis.renderer.labels.template.disabled = true;
          }}
          hasLegend={true}
          formatTooltipDate={formatTooltipDate(props.chartTimePeriod)}
          xAxisType='date'
          drawXAxis={(_, xAxis) => drawRelativeXAxisLabels(xAxis as am4charts.DateAxis, getDaysOfChartTimePeriod(props.chartTimePeriod), timeZoneOffset)}
          hasNoSelectionOverlay={false}
          useAxisTooltip
          adjustChartDimensions={ { height: 235 } }
        />
      </div>
    </div>
  );
}

const Button = styled(ChipButton)`
  z-index: 1000; /* visible over no selection overlay */
  margin-right: 0.75rem;

  &:nth-child(3) {
    margin-right: 3rem;
  }
`;
